<template>
  <OverviewHeader :title="$t('projectOverview.title')">
    <template #center>
      <TopBarQuickSearch entity-type="project" @update="$emit('update:quick-search', $event)" />
    </template>

    <template #actions>
      <SelectPopover
        :search="false"
        :modelValue="currentTypeFilter"
        :options="options"
        @update:modelValue="updateTypeFilter"
        @show="showTypeFilter = true"
        @hide="showTypeFilter = false"
        button-color="grey"
        type="single"
        size="fixed"
        label="project-type"
      >
        <template #button-content>
          <EyeShowIcon16 />
          {{ $t(`project.filter.${currentTypeFilter}`) }}
        </template>
      </SelectPopover>

      <FilterButton
        :number-of-filters="filter.filters.length"
        @open="showFilter = true"
        @clear="$emit('update:filter', { mode: 'all', filters: [] })"
      />

      <FilterOverlay
        v-model:open="showFilter"
        :items="items"
        :initial-filter="filter"
        :filter-options="filterOptions"
        @apply="
          $emit('update:filter', $event);
          showFilter = false;
        "
        entity-type="project"
      />

      <SortBar :modelValue="sorting" @update:modelValue="$emit('update:sorting', $event)" :options="sortOptions" />

      <PopoverMenu>
        <template #trigger>
          <Btn color="blue" data-test-id="add-project-button">
            {{ $t('action.addProject') }}
            <ChevronDownIcon16 />
          </Btn>
        </template>

        <template #items-list>
          <PopoverMenuSection :title="$t('action.importProjectA9')">
            <PopoverMenuText>
              {{ $t('projectOverview.import.description', { maxSizeMb }) }}
            </PopoverMenuText>
          </PopoverMenuSection>

          <PopoverMenuSection>
            <Btn
              @click="openImportFileDialog"
              layout="center"
              color="grey"
              data-test-id="import-project-button"
              style="width: 100%"
            >
              <UploadIcon16 />
              {{ $t('action.importProject') }}
            </Btn>
          </PopoverMenuSection>

          <PopoverMenuSection :title="$t('projectOverview.createProject.title')" divider>
            <PopoverMenuText>
              {{ $t('projectOverview.createProject.description') }}
            </PopoverMenuText>
          </PopoverMenuSection>

          <PopoverMenuSection>
            <Btn
              :title="$t('action.createProjectAlt', { shortcut: shortcuts['create:project'] })"
              @click="$store.dispatch('modal/show', { type: 'projectCreate', label: 'primary button' })"
              style="width: 100%"
              layout="center"
              color="grey"
              data-test-id="create-project-button"
              shortcut="create:project"
            >
              <AddIcon16 />
              {{ $t('action.createProject') }}
            </Btn>
          </PopoverMenuSection>
        </template>
      </PopoverMenu>

      <FileDrop :info="$t('projectImport.fileDrop.text')" @drop="handleFileDrop" />
    </template>
  </OverviewHeader>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { shortcuts } from '@shortcuts/index';
import sortOptions from './sortOptions';
import FilterOverlay from '@/components/FilterOverlay/index.vue';
import SortBar from '@components/SortBar.vue';
import AddIcon16 from '@components/Icons/AddIcon16.vue';
import UploadIcon16 from '@components/Icons/UploadIcon16.vue';
import EyeShowIcon16 from '@components/Icons/EyeShowIcon16.vue';
import ChevronDownIcon16 from '@components/Icons/ChevronDownIcon16.vue';
import { uploadFile } from '@components/Import/import';
import openFileDialog from '@utils/openFileDialog';
import getFileExtension from '@utils/getFileExtension';
import { FilterOptions, FilterList, HydratedFilter } from '@/filter/types';
import { createFilter } from '@/filter/createFilter';
import TopBarQuickSearch from '@components/TopBarQuickSearch.vue';
import FilterButton from '@components/FilterButton.vue';

const findTypeFilter = (filterInList: HydratedFilter) => filterInList.name === 'ProjectTypeFilter';

export default defineComponent({
  name: 'ProjectOverviewTopBar',

  emits: {
    'update:quick-search': null,
    'update:filter': null,
    'update:sorting': null,
    'filter-overlay-open': null,
  },

  components: {
    SortBar,
    AddIcon16,
    UploadIcon16,
    EyeShowIcon16,
    ChevronDownIcon16,
    FilterOverlay,
    TopBarQuickSearch,
    FilterButton,
  },

  props: {
    filter: {
      type: Object,
      required: true,
    },

    filterOptions: {
      type: Object as PropType<FilterOptions>,
      required: true,
    },

    items: {
      type: Array,
      required: true,
    },

    sorting: {
      type: Array,
      required: true,
    },
  },

  data(): {
    shortcuts: typeof shortcuts;
    showTypeFilter: boolean;
    showFilter: boolean;
    currentTypeFilter: string;
    maxSizeMb: string;
  } {
    const typeFilter = this.filter.filters.find(findTypeFilter);

    return {
      shortcuts,
      showTypeFilter: false,
      showFilter: false,
      currentTypeFilter: typeFilter?.value?.value?.[0] ?? 'all',
      maxSizeMb: `${this.$config.constants.maxFileSizeMb.atlprojImport} MB`,
    };
  },

  computed: {
    sortOptions() {
      return sortOptions();
    },

    options() {
      return [
        {
          key: 'all',
          label: this.$t('project.filter.all'),
          value: 'all',
        },
        {
          key: 'owned',
          label: this.$t('project.filter.owned'),
          value: 'owned',
        },
        {
          key: 'joined',
          label: this.$t('project.filter.joined'),
          value: 'joined',
        },
        {
          key: 'invited',
          label: this.$t('project.filter.invited'),
          value: 'invited',
        },
      ];
    },
  },

  methods: {
    updateTypeFilter(event: string) {
      this.showTypeFilter = false;

      const typeFilterIndex = this.filter.filters.findIndex(
        (filterInList) => filterInList.name === 'ProjectTypeFilter'
      );

      const filter = { ...this.filter };
      filter.filters = this.filter.filters.map((existingFilter) => ({ ...existingFilter }));

      if (typeFilterIndex > -1) {
        if (event === 'all') {
          // "All" filter does not exist in filter overlay
          filter.filters.splice(typeFilterIndex, 1);
        } else {
          filter.filters[typeFilterIndex].value.value = [event];
        }
      } else {
        const newFilter = createFilter('ProjectTypeFilter', 'item', {
          is: 'singleSelect',
        });

        newFilter.value.value = [event];

        filter.filters.push(newFilter);
      }

      this.$emit('update:filter', filter);
    },

    handleFileDrop(files: FileList) {
      // Only use the first file
      const file = files[0];

      const ext = getFileExtension(file.name);

      if (this.$config.constants.supportedImportFileEndings.includes(ext)) {
        uploadFile(file, this.$router);
      } else {
        this.$store.dispatch('notification/add', {
          message: this.$t('files.errors.notSupported', { name: file.name }),
        });
      }
    },

    openImportFileDialog() {
      openFileDialog(
        (files: FileList) => {
          uploadFile(files[0], this.$router);
        },
        'project-import-input',
        this.$config.constants.supportedImportFileEndings.reduce(
          (accumulated, current, index) => (index === 0 ? `.${current},` : `${accumulated}, .${current}`),
          ''
        ),
        false
      );
    },
  },

  watch: {
    /**
     * Update the route with the project type, if set
     */
    filter(value: FilterList) {
      const typeFilter = value.filters.find(findTypeFilter);

      const currentTypeFilter = (typeFilter?.value?.value as Array<string>)?.[0];

      if (currentTypeFilter !== this.currentTypeFilter) {
        if (this.currentTypeFilter === 'all' && !currentTypeFilter) {
          // 'all' and undefined is the same
          return;
        }

        this.currentTypeFilter = currentTypeFilter || 'all';

        this.$router.replace({
          name: 'projects',
          query: { type: currentTypeFilter },
        });
      }
    },

    showFilter(value) {
      this.$emit('filter-overlay-open', value);
    },
  },
});
</script>
